import { DateTime } from "luxon";
import type { BreakRulesConfig, HoursRateConfig, NurseRole, OvertimeConfig } from "../../../../../utils/models";
import { hppdTargetAverage, uniformHppdTarget } from "../../../../../utils/models";
import { breakRulesConfigFromApi, hoursRateConfigFromApi, overtimeConfigFromApi } from "../../../../../utils/wageRules/fromApi";
import type { GeneralFormData } from "../types";
import type { AirtableDiffRow } from "./airtableImportTypes";

/**
 * Merging accepted rows into FORM STATE — rather than hand-building an update payload — is load-bearing,
 * not stylistic.
 *
 * Four config fields have "omitted key = leave untouched" semantics on the backend's `exclude_unset`
 * dump: `auto_approve_rules`, `census_intervals`, `job_shift_times` and
 * `staff_schedule_display_until_date`. A payload assembled from just the accepted rows would omit the
 * others and, for the two that the save DOES always send, would send the wrong thing — wiping a
 * department's auto-approve rules or rolling back a published schedule horizon. Going through
 * `generalFormData` + the existing `submitDeptConfig` means the accepted rows are the ONLY delta, and
 * every one of those four fields keeps whatever the form already held.
 */

/** The two form fields held as Luxon `DateTime`, which therefore cannot take a raw JSON string. */
const DATE_FIELDS = new Set<keyof GeneralFormData>(["week1FirstSunday", "staffScheduleDisplayUntilDate"]);

/** Sentinel for two form fields that flatten to the same normalized string. */
const AMBIGUOUS = "__ambiguous__" as keyof GeneralFormData;

/**
 * Every field `GeneralFormData` declares, as a runtime-visible set.
 *
 * It exists because the live form object is NOT a faithful witness of the type: `units` and `floors`
 * are declared but `getFormDataFromDept` never assigns them on either branch, so an
 * `if (key in formData)` check refuses exactly those two rows — and the backend emits both. That was
 * a silent "this release has no setting for that field" on real data.
 *
 * `satisfies Record<keyof GeneralFormData, true>` is what keeps it honest: add a field to the type
 * without listing it here and the build fails, which is the opposite of the hand-maintained allowlist
 * this originally avoided.
 */
const FORM_FIELD_KEYS = {
	id: true,
	displayName: true,
	hospital: true,
	targetHPPD: true,
	targetHPPDPerDay: true,
	targetPPD: true,
	minValidHPPDPercent: true,
	maxValidHPPDPercent: true,
	week1FirstSunday: true,
	totalBeds: true,
	timezone: true,
	roles: true,
	defaultRole: true,
	notifyBeforeShiftStartMintutes: true,
	notifyBeforeShiftEndMintutes: true,
	partialBlockShifts: true,
	fullShiftTimes: true,
	jobShiftTimes: true,
	jobPpdOverrides: true,
	masterScheduleWeeks: true,
	canViewNurses: true,
	staffScheduleDisplayUntilDate: true,
	hideOverstaffingWarning: true,
	countBedholdInCensus: true,
	censusIntervals: true,
	combineConsecutiveAgencyShifts: true,
	agencyPostDelayDefaultHours: true,
	prnCycleDurationDays: true,
	roleGroups: true,
	hrisIntegrations: true,
	emrIntegrations: true,
	statsMode: true,
	removeUnionStaffWarning: true,
	departmentWageConfig: true,
	specialNurses: true,
	holidayBoundaries: true,
	dayShiftBoundaries: true,
	weekendBoundaries: true,
	weekStartDay: true,
	units: true,
	floors: true,
	callOffBypassHoursBeforeShiftBedside: true,
	callOffBypassHoursAfterStartBedside: true,
	callOffBypassHoursBeforeShiftAdmin: true,
	callOffBypassHoursAfterStartAdmin: true,
	callOffMessageLeadTimeMinutes: true,
	clipboardFacilityId: true,
	nursaFacilityId: true,
	hoursRateConfig: true,
	dayStart: true,
	payrollStartTime: true,
	overtimeConfig: true,
	// Not in the Airtable field map — the questionnaire never asks for it. Listed because this set is
	// the valid KEY SPACE `resolveFormKey` validates against, not the set we import, and the
	// `satisfies` guard below is what makes a new config field a compile error rather than a field
	// that silently resolves to nothing.
	overtimeEligibilityReleaseDays: true,
	overtimeEligibilityReleaseHours: true,
	breakRulesConfig: true,
	autoApproveRules: true,
	hrisPositions: true,
	lateArrivalMinutes: true,
	earlyDepartureMinutes: true,
	shiftDeltaMinutes: true,
	scheduledShiftDeltaMinutes: true,
	shiftDeltaThresholdHours: true,
	departmentType: true,
	jobIds: true,
	longShiftDuration: true,
	nurseInactivityAlertDays: true,
	nurseInactiveAfterDays: true,
	departmentDataHistoryWeeks: true,
	autoCreateScheduleWeeksAhead: true,
} satisfies Record<keyof GeneralFormData, true>;

/** Normalized form key → real key. Built once; `GeneralFormData` is static at runtime. */
const FORM_KEYS_BY_NORMALIZED = new Map<string, keyof GeneralFormData>();
for (const key of Object.keys(FORM_FIELD_KEYS) as (keyof GeneralFormData)[]) {
	const normalized = normalizeKey(key);
	// An ambiguous pair would make the lookup arbitrary; refuse both rather than guess (see below).
	FORM_KEYS_BY_NORMALIZED.set(normalized, FORM_KEYS_BY_NORMALIZED.has(normalized) ? AMBIGUOUS : key);
}

/**
 * Case- and separator-insensitive form of a key: `min_valid_hppd_percent` and `minValidHPPDPercent`
 * both become `minvalidhppdpercent`.
 *
 * A plain snake→camel conversion is NOT enough, and getting this wrong is silent: it produces
 * `minValidHppdPercent`, which is not a key of the form, so the row reports "this release has no
 * setting for that field" and quietly becomes un-appliable. The form is full of acronym keys
 * (`minValidHPPDPercent`, `targetHPPD`, `targetPPD`) whose capitalisation no rule can predict from the
 * snake_case side, so both sides are flattened instead of one being transformed into the other.
 */
function normalizeKey(key: string): string {
	return key.replaceAll("_", "").toLowerCase();
}

/**
 * The `GeneralFormData` key a row writes to, or `null` when the row names a field this form does not
 * have.
 *
 * Returning `null` (instead of merging anyway) is the safety property: an unrecognised `fieldKey`
 * cannot be spread into form state, where it would ride along on the next save as a key the config
 * endpoint never asked for. An AMBIGUOUS key is refused for the same reason — picking one of two
 * fields arbitrarily would write to the wrong one.
 */
export function resolveFormKey(fieldKey: string): keyof GeneralFormData | null {
	const match = FORM_KEYS_BY_NORMALIZED.get(normalizeKey(fieldKey));
	return match && match !== AMBIGUOUS ? match : null;
}

/**
 * A row the UI must not let anyone accept, whatever it says.
 *
 * `protected` is here for a different reason from the other three: they are rows whose VALUE could not
 * be used, while a protected row's value is perfectly good and this flow simply has no authority to
 * write that field. Same consequence — it can never enter the accepted set, so it can never reach
 * `mergeAcceptedRows` and never reach form state.
 */
const READ_ONLY_KINDS = new Set<AirtableDiffRow["kind"]>(["unresolvable", "out_of_band", "blocked", "protected"]);

export function isReadOnlyRow(row: AirtableDiffRow): boolean {
	return READ_ONLY_KINDS.has(row.kind);
}

/** The one date-shaped coercion: JSON string in, the Luxon `DateTime` the form holds out. */
function coerceDate(formKey: keyof GeneralFormData, raw: unknown): DateTime | null {
	if (raw === null || raw === undefined) return null;
	if (DateTime.isDateTime(raw)) return raw;
	if (typeof raw !== "string") throw new Error(`Airtable import: ${String(formKey)} expects a date string, got ${typeof raw}`);
	// The backend serializes dates as ISO; `yyyy-MM-dd` is the same parse under `fromISO`.
	const parsed = DateTime.fromISO(raw);
	if (!parsed.isValid) throw new Error(`Airtable import: ${String(formKey)} received an unparseable date "${raw}"`);
	return parsed;
}

/**
 * Coerce one incoming JSON value into the shape form state holds for that field.
 *
 * Two groups need it, and getting either wrong fails silently *at save time*, long after the
 * dialog closed and the accept decisions are gone:
 *
 * 1. **The two Luxon date fields.** `useUpdateGeneralForm` calls `.toFormat()` on them, so a raw
 *    `"2024-01-07"` string throws during the save.
 * 2. **The three rule lists.** The server's canonical `roles: []` means "all roles", but the form's
 *    validators read an empty `roles` as "nobody chose", push `rolesRequired` into
 *    `saveDisabledReasons`, and `submitDeptConfig` then returns without saving anything. The
 *    `*FromApi` converters exist to expand those markers — and their APP-450 note says BOTH entry
 *    points into form state must apply them, because the one that didn't made the page read as
 *    unsavable. This is the third entry point. They also normalize `"19:00:00"` to `"HH:MM"`.
 *
 * Throws rather than dropping: a value we cannot coerce is a bug in the diff, and a silent skip would
 * report "applied" for a field that was never written.
 */
export function coerceIncomingValue(formKey: keyof GeneralFormData, raw: unknown, deptRoles: NurseRole[]): unknown {
	if (DATE_FIELDS.has(formKey)) return coerceDate(formKey, raw);

	switch (formKey) {
		case "breakRulesConfig":
			return breakRulesConfigFromApi(raw as BreakRulesConfig[] | null, deptRoles);
		case "overtimeConfig":
			return overtimeConfigFromApi(raw as OvertimeConfig[] | null, deptRoles);
		case "hoursRateConfig":
			return hoursRateConfigFromApi(raw as HoursRateConfig[] | null, deptRoles);
		default:
			return raw;
	}
}

/** One row the operator said yes to, already resolved onto a form field. */
export type AcceptedRow = {
	row: AirtableDiffRow;
	formKey: keyof GeneralFormData;
};

/**
 * Apply the accepted rows onto a copy of the current form data.
 *
 * Pure — the caller owns when the result reaches React state, which matters: `submitDeptConfig` closes
 * over `generalFormData`, so the merge must land in state and the submit must fire on a LATER render.
 */
export function mergeAcceptedRows(formData: GeneralFormData, accepted: AcceptedRow[]): GeneralFormData {
	const merged: GeneralFormData = { ...formData };
	for (const { row, formKey } of accepted) {
		// Index assignment rather than a spread per row: `GeneralFormData` is wide, and re-spreading it
		// once per accepted field is quadratic for no benefit — the copy above is the only one needed.
		(merged as Record<string, unknown>)[formKey] = coerceIncomingValue(formKey, row.incomingValue, formData.roles ?? []);

		// `targetHPPD` is a backward-compat SCALAR that the save no longer decides anything with: the
		// backend reads `target_hppd_per_day or uniform(target_hppd_legacy)`, and `targetHPPDPerDay` is
		// non-optional in the form, so it always wins and the accepted value is discarded in silence.
		// Writing the per-day target too is what makes accepting this row mean anything — the same pair
		// the Settings form itself writes (`GeneralTabContent`'s `updateTargetHppdRows`).
		if (formKey === "targetHPPD" && typeof row.incomingValue === "number") {
			const perDay = uniformHppdTarget(row.incomingValue);
			merged.targetHPPDPerDay = perDay;
			merged.targetHPPD = hppdTargetAverage(perDay);
		}
	}
	return merged;
}
